/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Returns whether optional preference storage is currently permitted.
   * @returns {boolean}
   */
  static isPreferenceStorageAllowed() {
    const consentPolicy = window.__muhConsentPolicy;
    if (!consentPolicy || typeof consentPolicy.allows !== 'function') return false;
    if (consentPolicy.allows('preferences') !== true) return false;

    const customerPrivacy = window.Shopify?.customerPrivacy;
    if (!customerPrivacy || typeof customerPrivacy.preferencesProcessingAllowed !== 'function') return false;

    try {
      return customerPrivacy.preferencesProcessingAllowed() === true;
    } catch (_) {
      return false;
    }
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    const normalizedProductId = String(productId);
    if (!this.isPreferenceStorageAllowed() || !/^\d{1,32}$/.test(normalizedProductId)) return;

    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== normalizedProductId);
    viewedProducts.unshift(normalizedProductId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch (_) {
      // Preference storage can be unavailable in private or restricted contexts.
    }
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch (_) {
      // Nothing to clear when storage is unavailable.
    }
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    if (!this.isPreferenceStorageAllowed()) return [];

    try {
      const products = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      if (!Array.isArray(products)) return [];
      return products
        .map((productId) => String(productId))
        .filter((productId) => /^\d{1,32}$/.test(productId))
        .slice(0, this.#MAX_PRODUCTS);
    } catch (_) {
      return [];
    }
  }
}

const clearHistoryWhenPreferencesAreDenied = () => {
  const consentPolicy = window.__muhConsentPolicy;
  if (!consentPolicy || typeof consentPolicy.getState !== 'function') return;

  const consent = consentPolicy.getState();
  if (!consent) return;

  if (consent.preferences !== true) {
    RecentlyViewed.clearProducts();
    return;
  }

  const customerPrivacy = window.Shopify?.customerPrivacy;
  if (!customerPrivacy || typeof customerPrivacy.preferencesProcessingAllowed !== 'function') return;

  try {
    if (customerPrivacy.preferencesProcessingAllowed() === false) RecentlyViewed.clearProducts();
  } catch (_) {
    // Keep the record untouched while Shopify's privacy state is temporarily unavailable.
  }
};

window.addEventListener('muh-cookie-consent-change', clearHistoryWhenPreferencesAreDenied);
document.addEventListener('visitorConsentCollected', clearHistoryWhenPreferencesAreDenied);
clearHistoryWhenPreferencesAreDenied();
